import { api } from '../console/api.ts';
import { carrierCell, carrierName } from '../console/carriers.ts';
import { empty, meter, pageHead, pill } from '../console/components.ts';
import { esc, fmt, icon, type Money, q } from '../console/format.ts';
import {
  canQuote,
  followsRenewals,
  has,
  me,
  seesPortfolio,
  supervises,
} from '../console/session.ts';

/** «Resumen»: the agency's day at a glance — premium billed and collected by carrier, arrears and the day's accounting. */

type Figures = {
  policies: number;
  billed: number;
  collected: number;
  due: number;
  overdue: number;
  delinquent: number;
  /** Receipts the carrier collects that Nubi could not confirm lately (not in due or overdue). */
  unconfirmed?: number;
};

const ACCOUNT: Record<string, string> = {
  BANK: 'Bancos',
  CASH_BOX: 'Caja',
  PREMIUM_PAYABLE: 'Primas por remesar',
  COMMISSION_RECEIVABLE: 'Comisiones por cobrar',
  COMMISSION_INCOME: 'Ingresos por comisiones',
  CARRIER_BALANCE: 'Saldo a favor con aseguradora',
  ISR_WITHHELD_BY_CARRIERS: 'ISR retenido por aseguradoras',
  VAT_CREDIT: 'IVA crédito fiscal',
  AGENT_RECEIVABLE: 'Cuentas por cobrar a agentes',
  COMMISSION_PAYABLE: 'Comisiones por pagar a agentes',
  ISR_WITHHOLDING_PAYABLE: 'ISR retenido por pagar',
  AGENT_COMMISSION_EXPENSE: 'Gasto de comisiones a agentes',
  CASH_OVERAGE: 'Sobrantes de caja',
  CASH_SHORTAGE: 'Faltantes de caja',
};
/** "COMMISSION_RECEIVABLE:gyt" → "Comisiones por cobrar · Seguros G&T" */
const accountName = (key: string) => {
  const [code = '', sub] = key.split(':');
  return `${ACCOUNT[code] ?? code}${sub ? ` · ${carrierName(sub)}` : ''}`;
};

type OverdueByPerson = {
  total: number;
  items: { memberId: string; name: string | null; count: number }[];
};

/** «Tareas atrasadas por persona» (#317): who has tasks whose due hour passed, most first. */
function overdueCard(o: OverdueByPerson) {
  const top = Math.max(1, ...o.items.map((i) => i.count));
  return `<section class="card" data-component="overdue-tasks"><div class="card-head">${icon('calendar')}<h2>Tareas atrasadas por persona</h2><span class="spacer"></span>
    ${o.total ? pill('bad', o.total === 1 ? '1 atrasada' : `${o.total} atrasadas`) : pill('ok', 'Al día')}
    <button type="button" class="btn sm" data-go="mis-tareas">Ver tareas</button></div>
    ${
      o.items.length
        ? `<div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Tareas atrasadas de cada persona del equipo</caption><thead><tr><th>Persona</th><th class="num">Atrasadas</th></tr></thead><tbody>
      ${o.items.map((i) => `<tr><td>${esc(i.name ?? i.memberId)}</td><td class="num"><strong>${i.count}</strong>${meter(i.count / top, 'inline')}</td></tr>`).join('')}
      </tbody></table></div>`
        : '<p class="muted" style="padding:20px">Nadie del equipo tiene tareas atrasadas.</p>'
    }</section>`;
}

/**
 * What waits for someone today (#318), each item only when the person may see it (null: hidden):
 * the renewals coming, the receipts overdue, and the approvals pending — cancellations requested,
 * cash differences and commission advances. An agent sees only their own (the API filters).
 */
export type Pending = {
  renewals: { total: number; soon: number } | null;
  overdue: { receipts: number; amounts: Money[] } | null;
  cancellations: number | null;
  cashDifferences: number | null;
  advances: number | null;
};

const many = (n: number, one: string, other: string) => (n === 1 ? `1 ${one}` : `${n} ${other}`);

/** «Pendientes»: one row per kind of work waiting, with where to do it. */
export function pendingCard(p: Pending) {
  const rows: string[] = [];
  const row = (label: string, n: number, detail: string, view: string) =>
    rows.push(
      `<tr><td>${esc(label)}</td><td class="num"><strong>${n}</strong></td><td>${detail}</td><td class="num"><button type="button" class="btn sm" data-go="${view}">Ver</button></td></tr>`,
    );
  if (p.renewals)
    row(
      'Renovaciones próximas',
      p.renewals.total,
      p.renewals.total
        ? `${esc(many(p.renewals.soon, 'vence', 'vencen'))} en 30 días o menos`
        : 'Ninguna póliza vence en 60 días',
      'renovaciones',
    );
  if (p.overdue)
    row(
      'Cobros vencidos',
      p.overdue.receipts,
      p.overdue.receipts
        ? `${esc(p.overdue.amounts.map((m) => fmt(m)).join(' · '))} por cobrar`
        : 'Todo al día',
      'cartera',
    );
  if (p.cancellations !== null)
    row(
      'Cancelaciones solicitadas',
      p.cancellations,
      p.cancellations ? 'Esperan la decisión de la dirección' : 'Ninguna por decidir',
      'polizas',
    );
  if (p.cashDifferences !== null)
    row(
      'Diferencias de caja',
      p.cashDifferences,
      p.cashDifferences ? 'Cierres de caja por aprobar' : 'Ninguna por aprobar',
      'caja',
    );
  if (p.advances !== null)
    row(
      'Anticipos de comisión',
      p.advances,
      p.advances ? 'Solicitudes por aprobar' : 'Ninguno por aprobar',
      'liquidaciones',
    );
  if (!rows.length) return '';
  const waiting =
    (p.renewals?.soon ?? 0) +
    (p.overdue?.receipts ?? 0) +
    (p.cancellations ?? 0) +
    (p.cashDifferences ?? 0) +
    (p.advances ?? 0);
  return `<section class="card" data-component="pending"><div class="card-head">${icon('bell')}<h2>Pendientes</h2><span class="spacer"></span>
    ${waiting ? pill('warn', many(waiting, 'pendiente', 'pendientes')) : pill('ok', 'Al día')}</div>
    <div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Lo que espera hoy: renovaciones, cobros vencidos y aprobaciones</caption>
    <thead><tr><th>Pendiente</th><th class="num">Cantidad</th><th>Detalle</th><th class="num"><span class="sr-only">Ir</span></th></tr></thead>
    <tbody>${rows.join('')}</tbody></table></div></section>`;
}

/** Reads what waits (each request on its own: one that fails or is not allowed hides its row). */
async function pending(team: boolean): Promise<Pending> {
  const none = () => null;
  const agent = has('agent');
  const [renewals, arrears, cancellations, boxes, advances] = await Promise.all([
    followsRenewals()
      ? api<{ daysLeft: number }[]>('GET', '/v1/renewals').catch(none)
      : Promise.resolve(null),
    seesPortfolio()
      ? api<{ totals: { amount: Money; receipts: number }[] }>('GET', '/v1/arrears').catch(none)
      : Promise.resolve(null),
    team || agent
      ? api<unknown[]>('GET', '/v1/cancellation-requests').catch(none)
      : Promise.resolve(null),
    supervises()
      ? api<{ state: string }[]>('GET', '/v1/cash-boxes').catch(none)
      : Promise.resolve(null),
    team || agent
      ? api<{ state: string }[]>('GET', '/v1/commission-advances').catch(none)
      : Promise.resolve(null),
  ]);
  const amounts = new Map<string, number>();
  for (const t of arrears?.totals ?? [])
    amounts.set(t.amount.currency, (amounts.get(t.amount.currency) ?? 0) + t.amount.amountMinor);
  return {
    renewals: renewals && {
      total: renewals.length,
      soon: renewals.filter((r) => r.daysLeft <= 30).length,
    },
    overdue: arrears && {
      receipts: arrears.totals.reduce((n, t) => n + t.receipts, 0),
      amounts: (['GTQ', 'USD'] as const)
        .filter((c) => amounts.has(c))
        .map((currency) => ({ amountMinor: amounts.get(currency) ?? 0, currency })),
    },
    cancellations: cancellations && cancellations.length,
    cashDifferences: boxes && boxes.filter((b) => b.state === 'PENDING_APPROVAL').length,
    advances: advances && advances.filter((a) => a.state === 'REQUESTED').length,
  };
}

export async function resumen() {
  if (!me) return '';
  const admin = has('agency_director', 'agency_admin');
  // The team's overdue tasks per person (#317): management only; never blocks the board.
  const team = has('agency_director', 'agency_admin', 'agency_manager');
  const [stats, tb, overdue, waiting] = await Promise.all([
    api<{
      totals: (Figures & { currency: string })[];
      byCarrier: (Figures & { carrier: string; currency: string })[];
    }>('GET', '/v1/stats'),
    admin
      ? api<{ balanced: boolean; rows: { account: string; balance: Money }[] }>(
          'GET',
          '/v1/ledger/trial-balance',
        )
      : Promise.resolve(null),
    team
      ? api<OverdueByPerson>('GET', '/v1/tasks/overdue-by-person').catch(() => null)
      : Promise.resolve(null),
    pending(team),
  ]);
  const quote = canQuote()
    ? `<button type="button" class="btn primary" data-go="cotizar">${icon('bolt')}Nueva cotización</button>`
    : '';
  let html = pageHead(
    me.agentId ? 'Mi cartera' : 'Resumen de la agencia',
    me.agentId
      ? 'Tus pólizas, lo que falta cobrar y lo que está vencido.'
      : 'Primas facturadas y cobradas por aseguradora, mora y contabilidad del día.',
    quote,
  );
  // Quetzales and dollars are never added: one row of indicators per currency.
  const CURRENCY: Record<string, string> = { GTQ: 'En quetzales', USD: 'En dólares' };
  const multi = stats.totals.length > 1;
  const totals = stats.totals.length
    ? stats.totals
    : [
        {
          currency: 'GTQ',
          policies: 0,
          billed: 0,
          collected: 0,
          due: 0,
          overdue: 0,
          delinquent: 0,
        },
      ];
  for (const t of totals) {
    const m = (minor: number) => q(minor, t.currency);
    const ratio = t.billed ? t.collected / t.billed : 0;
    const late = t.overdue + t.delinquent;
    const carriers = stats.byCarrier.filter((c) => c.currency === t.currency).length;
    const label = CURRENCY[t.currency] ?? t.currency;
    html += `${multi ? `<h2 class="kpi-group">${esc(label)}</h2>` : ''}<section class="kpis" aria-label="${esc(multi ? `Indicadores ${label.toLowerCase()}` : 'Indicadores')}">
    <div class="card kpi"><span class="kpi-label">${icon('shield')}Pólizas vigentes</span><span class="kpi-value">${t.policies}</span><span class="kpi-foot">${carriers === 1 ? '1 aseguradora' : `${carriers} aseguradoras`}</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('receipt')}Prima facturada</span><span class="kpi-value">${m(t.billed)}</span><span class="kpi-foot">${m(t.due)} por cobrar</span></div>
    <div class="card kpi"><span class="kpi-label">${icon('trend')}Cobrado</span><span class="kpi-value">${m(t.collected)}</span>${meter(ratio)}<span class="kpi-foot">${Math.round(ratio * 100)} % de lo facturado</span></div>
    <div class="card kpi ${late ? 'warn' : ''}"><span class="kpi-label">${icon('warn')}Vencido y en mora</span><span class="kpi-value">${m(late)}</span><span class="kpi-foot">${late ? `${m(t.delinquent)} en mora` : 'Todo al día'}${t.unconfirmed ? ` · ${m(t.unconfirmed)} sin confirmar con la aseguradora` : ''}</span></div>
  </section>`;
  }
  html += pendingCard(waiting);
  if (overdue) html += overdueCard(overdue);
  html += stats.byCarrier.length
    ? `<section class="card"><div class="card-head"><h2>Por aseguradora</h2></div><div class="table-wrap" data-component="table"><table class="table">
      <thead><tr><th>Aseguradora</th><th class="num">Pólizas</th><th class="num">Facturado</th><th class="num">Cobrado</th><th class="num">Por cobrar</th><th class="num">Vencido</th></tr></thead><tbody>
      ${stats.byCarrier
        .map((c) => {
          const r = c.billed ? c.collected / c.billed : 0;
          const lateC = c.overdue + c.delinquent;
          const m = (minor: number) => q(minor, c.currency);
          return `<tr><td>${carrierCell(c.carrier)}${multi ? ` <span class="muted small">${c.currency === 'USD' ? 'dólares' : 'quetzales'}</span>` : ''}</td><td class="num">${c.policies}</td><td class="num">${m(c.billed)}</td>
          <td class="num">${m(c.collected)}${meter(r, 'inline')}</td><td class="num">${m(c.due)}</td>
          <td class="num">${lateC ? `<strong style="color:var(--warn)">${m(lateC)}</strong>` : m(0)}</td></tr>`;
        })
        .join('')}</tbody></table></div></section>`
    : empty(
        'inbox',
        'Todavía no hay pólizas',
        'Cuando emitas la primera, aquí verás lo facturado, lo cobrado y la mora por aseguradora.',
        quote,
        2,
      );
  if (tb)
    html += `<section class="card"><div class="card-head">${icon('scale')}<h2>Balanza de comprobación</h2><span class="spacer"></span>
      ${tb.balanced ? pill('ok', 'Cuadra') : pill('bad', 'No cuadra')}</div>
      ${
        tb.rows.length
          ? `<div class="table-wrap" data-component="table"><table class="table"><thead><tr><th>Cuenta</th><th class="num">Saldo</th></tr></thead><tbody>
        ${tb.rows.map((r) => `<tr><td>${esc(accountName(r.account))}</td><td class="num">${fmt(r.balance)}</td></tr>`).join('')}
        </tbody></table></div>`
          : '<p class="muted" style="padding:20px">Sin movimientos todavía.</p>'
      }</section>`;
  return html;
}
